import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Award, Edit2, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = {
  bronce: 'Bronce · 3% cashback',
  plata: 'Plata · 5%',
  oro: 'Oro · 10%',
  platino: 'Platino · 20%',
  pro: 'Pro · 30%',
};

const TIER_COLOR = {
  bronce: 'bg-amber-100 text-amber-900 border-amber-400',
  plata: 'bg-gray-200 text-gray-800 border-gray-400',
  oro: 'bg-yellow-100 text-yellow-900 border-yellow-500',
  platino: 'bg-slate-200 text-slate-900 border-slate-500',
  pro: 'bg-black text-white border-black',
};

export default function AdminLoyalty({ user, setUser }) {
  const [members, setMembers] = useState([]);
  const [cycleInfo, setCycleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    fitz_points: 0,
    tier: 'bronce',
    tier_expires_at: '',
    manual_override: false,
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchMembers();
  }, [user, navigate]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/loyalty/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
        setCycleInfo({
          cycle_year: data.cycle_year,
          cycle_start: data.cycle_start,
          tier_thresholds: data.tier_thresholds,
          tier_discounts: data.tier_discounts,
        });
      } else {
        toast.error('No se pudo cargar la lista');
      }
    } catch (e) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      fitz_points: m.fitz_points,
      tier: m.tier,
      tier_expires_at: m.tier_expires_at ? m.tier_expires_at.substring(0, 10) : '',
      manual_override: m.manual_override,
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        fitz_points: parseFloat(form.fitz_points),
        tier: form.tier,
        manual_override: form.manual_override,
      };
      if (form.tier_expires_at) {
        payload.tier_expires_at = new Date(form.tier_expires_at).toISOString();
      } else {
        payload.tier_expires_at = null;
      }
      const res = await fetch(`${BACKEND_URL}/api/admin/loyalty/members/${editing.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Cliente actualizado');
        setEditing(null);
        fetchMembers();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (e) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleRecompute = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/admin/loyalty/members/${userId}/recompute`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        toast.success('Recalculado');
        fetchMembers();
      }
    } catch (e) {
      toast.error('Error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const filteredMembers = members.filter((m) => {
    const matchesSearch =
      !search ||
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase());
    const matchesTier = tierFilter === 'all' || m.tier === tierFilter;
    return matchesSearch && matchesTier;
  });

  const tierCounts = members.reduce((acc, m) => {
    acc[m.tier] = (acc[m.tier] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
            ADMIN - ARTHUR FITZ
          </h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">
              Ver Tienda
            </Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6 flex-wrap">
            <Link to="/admin" className="text-gray-600 hover:text-black">
              Dashboard
            </Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black">
              Productos
            </Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black">
              Pedidos
            </Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black">
              Clientes
            </Link>
            <Link to="/admin/loyalty" className="font-medium text-black border-b-2 border-black pb-2">
              ArtFitz Club
            </Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">
              Cupones
            </Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">
              Envíos
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
              <Award className="w-8 h-8" /> ArtFitz Club
            </h2>
            {cycleInfo && (
              <p className="text-sm text-gray-600 mt-1">
                Ciclo actual: 1 Abr {cycleInfo.cycle_year} – 31 Mar {cycleInfo.cycle_year + 1}
              </p>
            )}
          </div>
        </div>

        {/* Tier summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {['bronce', 'plata', 'oro', 'platino', 'pro'].map((t) => (
            <div
              key={t}
              className={`border-2 p-3 text-center ${TIER_COLOR[t]}`}
              data-testid={`tier-count-${t}`}
            >
              <p className="text-xs uppercase tracking-wider">{t}</p>
              <p className="text-2xl font-bold">{tierCounts[t] || 0}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="loyalty-search-input"
            />
          </div>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger className="w-full md:w-[200px]" data-testid="loyalty-tier-filter">
              <SelectValue placeholder="Filtrar por nivel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los niveles</SelectItem>
              <SelectItem value="bronce">Bronce</SelectItem>
              <SelectItem value="plata">Plata</SelectItem>
              <SelectItem value="oro">Oro</SelectItem>
              <SelectItem value="platino">Platino</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p>Cargando...</p>
        ) : (
          <div className="bg-white border-2 border-black overflow-x-auto" data-testid="loyalty-members-table">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black bg-gray-50">
                  <th className="text-left p-3">Cliente</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-right p-3">Fitz Points</th>
                  <th className="text-center p-3">Nivel</th>
                  <th className="text-right p-3">Pedidos ciclo</th>
                  <th className="text-right p-3">€ ciclo</th>
                  <th className="text-center p-3">Caducidad</th>
                  <th className="text-center p-3">Manual</th>
                  <th className="text-center p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((m) => (
                  <tr key={m.id} className="border-b hover:bg-gray-50" data-testid={`loyalty-row-${m.id}`}>
                    <td className="p-3 font-medium">{m.name}</td>
                    <td className="p-3 text-gray-600">{m.email}</td>
                    <td className="p-3 text-right font-mono">{m.fitz_points.toFixed(2)}</td>
                    <td className="p-3 text-center">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold border ${TIER_COLOR[m.tier]}`}
                      >
                        {m.tier.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-right">{m.cycle_orders_count}</td>
                    <td className="p-3 text-right">{m.cycle_orders_total.toFixed(2)}€</td>
                    <td className="p-3 text-center text-sm">
                      {m.tier_expires_at
                        ? new Date(m.tier_expires_at).toLocaleDateString('es-ES')
                        : '—'}
                    </td>
                    <td className="p-3 text-center">
                      {m.manual_override ? (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Sí</span>
                      ) : (
                        <span className="text-xs text-gray-400">No</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex gap-2 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(m)}
                          data-testid={`edit-loyalty-${m.id}`}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRecompute(m.id)}
                          title="Recalcular"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredMembers.length === 0 && (
                  <tr>
                    <td colSpan="9" className="p-6 text-center text-gray-500">
                      Sin resultados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-loyalty-dialog">
          <DialogHeader>
            <DialogTitle>
              Editar cliente: {editing?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fitz Points</Label>
              <Input
                type="number"
                step="0.01"
                value={form.fitz_points}
                onChange={(e) => setForm({ ...form, fitz_points: e.target.value })}
                data-testid="edit-points-input"
              />
            </div>
            <div>
              <Label>Nivel</Label>
              <Select
                value={form.tier}
                onValueChange={(v) => setForm({ ...form, tier: v })}
              >
                <SelectTrigger data-testid="edit-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(TIER_LABELS).map((t) => (
                    <SelectItem key={t} value={t}>
                      {TIER_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Caducidad del nivel (oro/platino/pro)</Label>
              <Input
                type="date"
                value={form.tier_expires_at}
                onChange={(e) => setForm({ ...form, tier_expires_at: e.target.value })}
                data-testid="edit-expires-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Para oro/platino/pro, el nivel se conserva hasta esta fecha.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="manual_override"
                checked={form.manual_override}
                onCheckedChange={(v) => setForm({ ...form, manual_override: !!v })}
                data-testid="edit-manual-checkbox"
              />
              <Label htmlFor="manual_override" className="cursor-pointer">
                Asignación manual (ignorar reglas automáticas)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-black text-white hover:bg-gray-800"
              data-testid="save-loyalty-btn"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
